import { useState } from 'react';

import { Button, ButtonSegment } from '@poseidon/components/Input2';

import { DaysNumberInput } from '~/components/Sequence/DaysNumberInput';
import { MAX_DAYS, MAX_WEEKS, commitWaitValue, fromDays, reexpressWaitForUnit, type WaitUnit } from './waitDuration';

export type { WaitUnit };

/**
 * Compact wait-duration editor: a number + a days/weeks unit toggle. The value stored on the step is
 * always canonical DAYS (`waitDays`, what the engine waits) — weeks are `value * 7`; `unit` is a display
 * preference. Edits locally while typing and commits (clamped) on blur / Enter / unit change, so we don't
 * fire a save on every keystroke. The days↔weeks math lives in {@link ./waitDuration} (unit-tested).
 */
export function WaitDurationInput({
  days,
  unit,
  onCommit,
  autoFocus,
}: {
  days: number;
  unit: WaitUnit;
  onCommit: (days: number, unit: WaitUnit) => void;
  /** Focus + select the number field on mount — set when opened in a popover so it's ready to type. */
  autoFocus?: boolean;
}) {
  const [value, setValue] = useState(String(fromDays(days, unit)));

  const commit = () => {
    const { value: next, days: canonicalDays } = commitWaitValue(value, unit, days);
    setValue(String(next));
    if (canonicalDays !== days) onCommit(canonicalDays, unit);
  };

  // Re-express the current value in the newly-picked unit (days↔weeks) and commit the switch.
  const switchUnit = (nextUnit: WaitUnit) => {
    if (nextUnit === unit) return;
    const { value: nextValue, days: canonicalDays } = reexpressWaitForUnit(value, unit, nextUnit, days);
    setValue(String(nextValue));
    onCommit(canonicalDays, nextUnit);
  };

  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.preventDefault()}>
      <DaysNumberInput
        value={value}
        onChange={setValue}
        onCommit={commit}
        min={1}
        max={unit === 'weeks' ? MAX_WEEKS : MAX_DAYS}
        autoFocus={autoFocus}
        className="w-12 text-center"
      />
      {/* A two-button toggle (not a dropdown) — both labels fit fully; it lives in a popover so width is free. */}
      <ButtonSegment<WaitUnit> size="xs" selectedId={unit} onChange={switchUnit}>
        <Button id="days">days</Button>
        <Button id="weeks">weeks</Button>
      </ButtonSegment>
    </div>
  );
}
